import {
  Box,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  SxProps,
  Theme,
} from '@mui/material';
import { IconButtonStyledOutlined } from './iconButtonStyledOutlined';
import { ReactNode } from 'react';
import ViewModeBar from './ViewModeBar';
import { ViewMode } from '@/constants/enum';

export type HeaderIcon = {
  id: string;
  svgPath: ReactNode;
  onClick: () => void;
};

export interface WidgetHeaderProps {
  /**
   * The title of the widget
   */
  title: string | ReactNode;

  /**
   * Optional status message or subtitle to display below the title
   */
  statusMessage?: ReactNode;

  /**
   * Optional color for the status message
   */
  statusColor?: string;

  /**
   * Array of header icons to display
   */
  headerIcons?: HeaderIcon[];

  /**
   * Optional children to render on the right side of the header
   */
  rightContent?: ReactNode;

  /**
   * Optional children to render on the left side of the header
   */
  leftContent?: ReactNode;

  /**
   * View mode configuration
   */
  viewMode?: {
    currentViewMode: ViewMode;
    setViewMode: (mode: ViewMode) => void;
    viewModes: ViewMode[];
  };

  /**
   * Optional sx props for the container
   */
  sx?: SxProps<Theme>;
}

const WidgetHeader = ({
  title,
  statusMessage,
  statusColor,
  headerIcons = [],
  rightContent,
  leftContent,
  viewMode,
  sx,
}: WidgetHeaderProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const baseGap = statusMessage ? 3 : 2;

  return (
    <Stack
      direction='column'
      gap={isMobile ? 1.5 : baseGap}
      sx={sx}
    >
      {/* Row 1: Title on the left, optional right-side content (e.g. Add button) */}
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='top'
        flexWrap={isMobile ? 'wrap' : 'nowrap'}
        rowGap={1}
        columnGap={2}
      >
        <Typography
          variant='h5'
          sx={{
            fontSize: { xs: '20px', sm: '24px' },
            fontWeight: 600,
            lineHeight: '133.4%',
            minWidth: 0,
            flexShrink: 1,
          }}
        >
          {title}
        </Typography>

        {rightContent && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {rightContent}
          </Box>
        )}
      </Stack>

      {/* Row 2: Left-side content (e.g. Custom Group dropdown) and view mode / icons on the right */}
      {(leftContent || viewMode || headerIcons.length > 0) && (
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems={isMobile ? 'flex-start' : 'center'}
          flexWrap={isMobile ? 'wrap' : 'nowrap'}
          rowGap={1}
          columnGap={2}
        >
          {/* Left section */}
          {leftContent && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {leftContent}
            </Box>
          )}

          {/* Right section: view mode toggle and any header icons */}
          {(viewMode || headerIcons.length > 0) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {viewMode && (
                <ViewModeBar
                  currentViewMode={viewMode.currentViewMode}
                  setViewMode={viewMode.setViewMode}
                  viewModes={viewMode.viewModes}
                />
              )}
              {headerIcons.map((icon) => (
                <IconButtonStyledOutlined
                  key={icon.id}
                  variant='outlined'
                  onClick={icon.onClick}
                >
                  {icon.svgPath}
                </IconButtonStyledOutlined>
              ))}
            </Box>
          )}
        </Stack>
      )}

      {/* Status message (if any) */}
      {statusMessage && (
        <Typography
          variant='subtitle1'
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: statusColor || theme.palette.text.primary,
          }}
        >
          {statusMessage}
        </Typography>
      )}
    </Stack>
  );
};

export default WidgetHeader;
